import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import * as Sentry from '@sentry/react';

let isInitialized = false;

/**
 * Initialize LogRocket if it hasn't been initialized yet.
 * This should be called early in the app lifecycle, but only if the organization has the feature enabled.
 */
export function initializeLogRocket() {
	// Only initialize once per page load
	if (isInitialized) {
		return;
	}

	// Only initialize if enabled in the environment (production/staging)
	if (!process.env.LOGROCKET_ENABLED) {
		return;
	}

	// Check if the organization has LogRocket enabled via meta tag
	// This is set by Django template in base.html
	const logRocketEnabledMeta = document.querySelector('meta[name="logrocket-enabled"]');
	const isLogRocketEnabled = logRocketEnabledMeta && logRocketEnabledMeta.getAttribute('content') === 'true';
	
	if (!isLogRocketEnabled) {
		return;
	}

	try {
		const version = document.querySelector('meta[name="frontend-version"]')?.content;

		LogRocket.init('ejkxj0/plecto-trials', {
			release: version ? `plecto-frontend@${version}` : undefined,

			// Network request sanitization
			network: {
				requestSanitizer: request => {
				// Ignore localhost requests (e.g., from browser extensions)
					const url = request.url.toLowerCase();
					if (url.includes('localhost') || url.includes('127.0.0.1')) {
						return null; // Returning null ignores the request/response pair
					}

					// Sanitize headers containing sensitive data
					if (request.headers) {
						delete request.headers['Authorization'];
						delete request.headers['X-CSRFToken'];
					}
					return request;
				},
				responseSanitizer: response => {
				// Sanitize response data if needed
					return response;
				},
			},

			// Console sanitization
			console: {
				shouldAggregateConsoleErrors: true,
			},

			// Don't capture input values from sensitive fields
			dom: {
				textSanitizer: true,
				inputSanitizer: true,
			},

			// Don't capture IP addresses
			shouldCaptureIP: false,
		});

		// Set up React plugin for better component tracking
		setupLogRocketReact(LogRocket);

		// Integration with Sentry - attach LogRocket URL to Sentry errors
		if (process.env.SENTRY_ENABLED) {
			LogRocket.getSessionURL(sessionURL => {
				if (sessionURL) {
					Sentry.setExtra('LogRocket', sessionURL);
					Sentry.setContext('LogRocket', {
						sessionURL: sessionURL,
					});
				}
			});
		}

		isInitialized = true;
	} catch (err) {
		console.error('Failed to initialize LogRocket', err);
	}
}

/**
 * Identify a user in LogRocket.
 * This should be called after initialization with user details.
 */
export function identifyLogRocketUser(userId, userInfo = {}) {
	if (isInitialized) {
		try {
			// Include environment in user traits
			const traits = {
				...userInfo,
				environment: process.env.LOGROCKET_ENVIRONMENT,
			};
			LogRocket.identify(userId, traits);
		} catch (err) {
			console.error('Failed to identify LogRocket user', err);
		}
	}
}

/**
 * Log a message to LogRocket
 */
export function logRocketLog(message, data) {
	if (isInitialized) {
		try {
			LogRocket.log(message, data);
		} catch (err) {
			console.error('Failed to log to LogRocket', err);
		}
	}
}

/**
 * Get the current LogRocket session URL
 */
export function getLogRocketSessionURL(callback) {
	if (isInitialized) {
		try {
			LogRocket.getSessionURL(callback);
		} catch (err) {
			console.error('Failed to get LogRocket session URL', err);
		}
	}
}

/**
 * Check if LogRocket is currently active
 */
export function isLogRocketActive() {
	return isInitialized;
}

// Auto-initialize on import if conditions are met
initializeLogRocket();
